import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { classroomConnectionActions } from "../../public/state/classroom.actions";
import { restoreLessonActions } from "../../public/store/lesson.actions";
import { catchError, filter, from, map, of, switchMap } from "rxjs";
import { StreamingHubService } from "@pcp/streaming";
import { authServiceActions } from "@pcp/auth";
import { isNotNullOrUndefined } from "@pcp/core/util";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Store } from "@ngrx/store";
import { PCP_LOGGER } from "@pcp/logging";

@Injectable()
export class ConnectToClassroomEffects {

    private readonly log = inject(PCP_LOGGER).createScoped('classroom');
    private readonly actions$ = inject(Actions);
    private readonly signalr = inject(StreamingHubService);
    private readonly store = inject(Store);
    private readonly snackbar = inject(MatSnackBar);

    /**
     * Connect to the classroom after login when the lessonId is included in the token data.
     */
    readonly connectToClassroomAfterLogin$ = createEffect(() => this.actions$.pipe(
        ofType(
            authServiceActions.userLoginSuccess,
            authServiceActions.tokenRestoreSuccess,
        ),
        map(({ tokenData }) => tokenData.claims.lessonId),
        filter(isNotNullOrUndefined),
        map(lessonId => classroomConnectionActions.connectToClassroom({ lessonId })),
    ));

    /**
     * Connect to the classroom after a running lesson was restored from the
     * API (page reload / re-login while a lesson is active).
     */
    readonly connectToClassroomAfterRestore$ = createEffect(() => this.actions$.pipe(
        ofType(restoreLessonActions.restoreLessonStateSuccess),
        map(({ lesson: { lessonId } }) => classroomConnectionActions.connectToClassroom({ lessonId })),
    ));

    readonly connectToClassroom$ = createEffect(() => this.actions$.pipe(
        ofType(classroomConnectionActions.connectToClassroom),
        switchMap(({ lessonId }) => from(this.signalr.joinLesson(lessonId)).pipe(
            map(lessonResponse => classroomConnectionActions.connectToClassroomSuccess({
                lessonId: lessonResponse.id,
                studentRegistrationCode: lessonResponse.studentRegistrationCode,
                customer: lessonResponse.customer,
                teacher: lessonResponse.teacher,
                start: lessonResponse.start,
                presentationId: lessonResponse.presentationId,
            })),
            catchError(error => {
                this.log.error('Failed to connect to classroom', error instanceof Error ? error : undefined, { error });
                return of(classroomConnectionActions.connectToClassroomFailed({ error }));
            }),
        )),
    ));

    /**
     * StreamingHub automatically reconnects to the active lesson ID (if any)
     * after each connection loss.
     *
     * If e.g. the lesson got stopped in the meantime,
     * `reconnectToLessonFailed$` is fired.
     *
     * This efect makes sure, the store and the UI gets updated accordingly.
     */
    readonly reconnectToLessonFailed$ = createEffect(() => this.signalr.reconnectToLessonFailed$.pipe(
        map(error => classroomConnectionActions.connectToClassroomFailed({ error })),
    ));
}
